'use client';

import { use } from 'react';
import Block from '@/components/elements/Block';
import { useCart } from '@/contexts/CartContext';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice } from '@/utils/utilities.mjs';
import CartContent from './CartContent';
import CartOrderTotals from './CartOrderTotals';
import CartCheckoutButton from './CartCheckoutButton';
import EmptyState from './EmptyState';
import SecureCheckoutBadges from './SecureCheckoutBadges';
import ContactCX from '@/components/global/cx/ContactCX';

export default function CartSummaryClient({
  block,
  position,
  cartFromServer,
  variantLabels,
}) {
  const pricing = usePricing();
  const { cart: cartFromContext, isCartLoading, isClearingCart } = useCart();
  const cartFromServerResolved = use(cartFromServer);
  const cart = cartFromContext ?? cartFromServerResolved;
  // BL-757: keyed by Shopify product id, not line id, so it still applies when
  // the cart below comes from context — including the extra line `+` creates,
  // which re-adds a product the map already covers. Only a product the server
  // render never saw would fall back to Shopify's variant title, and neither
  // add path produces one: AddToCartButton is PDP-only and navigates to /cart
  // after the add resolves.
  const resolvedVariantLabels = use(variantLabels);
  const { settings, customer_service_chat } = block;
  const cxChat =
    customer_service_chat?.reference?.[0] ?? customer_service_chat?.[0];
  const showCxChat = cxChat && cxChat.visible !== false;

  const summarySurfaceClass =
    settings?.background_color?.color_selector || 'bg-warm-white';

  const dockClasses = [
    'max-lg:sticky max-lg:bottom-0 max-lg:z-20 max-lg:w-[calc(100%+40px)]',
    'max-lg:px-5 max-lg:pt-3 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))]',
    `max-lg:shadow-[0_-4px_4px_rgba(0,0,0,0.08)] lg:hidden ${summarySurfaceClass}`,
  ].join(' ');

  const totalBeforeTax = formatPrice(pricing.cart.total(cart));

  if ((!cart || cart?.totalQuantity < 1) && !isCartLoading && !isClearingCart) {
    return <EmptyState position={position} block={block} />;
  }

  block = {
    ...block,
    _content_type_uid: 'cart_summary',
  };

  const overriddenSettings = {
    ...settings,
    block_top_padding: 'Zero',
    block_bottom_padding: 'Zero',
  };

  return (
    <Block settings={overriddenSettings} name="cart-summary">
      <div className="flex w-full flex-col items-center px-5 py-6 lg:flex-row lg:items-start lg:justify-center lg:py-15">
        <CartContent cart={cart} variantLabels={resolvedVariantLabels} />

        <div className="w-full lg:w-100 flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
          <CartOrderTotals cart={cart} />

          {showCxChat && (
            <div className="hidden lg:block">
              <ContactCX block={cxChat} />
            </div>
          )}
        </div>

        <div
          className={dockClasses}
          role="region"
          aria-label="Checkout"
        >
          <div className="w-full max-lg:px-4">
            <div className="summary-item sh10 order-total-section mb-3 flex w-full justify-between">
              <dt className="font-worksans text-primary font-semibold">
                Total Before Tax
              </dt>
              <dd
                aria-live="polite"
                aria-atomic="true"
                className="font-worksans text-primary font-semibold"
              >
                {totalBeforeTax}
              </dd>
            </div>
            <CartCheckoutButton id="checkout-dock" />
            {/* Badges below the button match production and naturally push the
                button above Kodif's fixed launcher (~60px at bottom:15px). */}
            <SecureCheckoutBadges className="mt-3 pb-6" />
          </div>
        </div>

        {showCxChat && (
          <div className="w-full lg:hidden mt-6">
            <ContactCX block={cxChat} />
          </div>
        )}
      </div>
    </Block>
  );
}
